import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { BuilderComponent, builder, useIsPreviewing } from '@builder.io/react';
import DefaultErrorPage from 'next/error';
import { WEBSITE_URL, STATIC_FILE_URL } from '@/shared/constants';
import CustomHead from '@/components/Common/Layout/Head';
import { logPageView } from '@/lib/service';
import { Layout } from '@/components/Common';
import { SEO_META } from '@/shared/seo';
import { SEO_META_PAGES } from '@/shared/types/seo';
import { BuilderContent } from '@builder.io/sdk';
import { GetStaticProps } from 'next';
import '../builder-registry';

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

// Define a function that fetches the Builder
// content for a given page
export const getStaticProps: GetStaticProps = async ({ params }) => {
  // Fetch the builder content for the given page
  const page = await builder
    .get('page', {
      userAttributes: {
        urlPath: '/' + ((params?.page as string[])?.join('/') || ''),
      },
    })
    .toPromise();

  // Return the page content as props
  return {
    props: {
      page: page || null,
    },
    // Revalidate the content every 5 minutes
    revalidate: 300,
  };
};

// Define a function that generates the
// static paths for all pages in Builder
export async function getStaticPaths() {
  // Get a list of all pages in Builder
  const pages = await builder.getAll('page', {
    // We only need the URL field
    fields: 'data.url',
    options: { noTargeting: true },
  });

  // Generate the static paths for all pages in Builder
  return {
    paths: pages.map((page) => String(page.data?.url)).filter((url) => url !== '/'),
    fallback: 'blocking',
  };
}

// Define the Page component
export default function Page({ page }: { page: BuilderContent | null }) {
  const router = useRouter();
  const isPreviewing = useIsPreviewing();
  useEffect(() => {
    logPageView({
      pageUrl: window.location.href || '',
      routeName: window.location.pathname,
      pageTitle: `${page?.data?.title} | Pietra`,
    });
  }, [page?.data?.title]);

  // If the page content is not available
  // and not in preview mode, show a 404 error page
  if (!page && !isPreviewing) {
    return <DefaultErrorPage statusCode={404} />;
  }

  // If the page content is available, render
  // the BuilderComponent with the page content
  return (
    <>
      <CustomHead
        title={page?.data?.title}
        description={page?.data?.description}
        url={`${WEBSITE_URL}${router.asPath}`}
        image={page?.data?.image}
      />
      {/* Render the Builder page */}
      <Layout structured={SEO_META[SEO_META_PAGES.PIETRA_PARTNERS].jsonld()}>
        <BuilderComponent model="page" content={page || undefined} />
      </Layout>
    </>
  );
}
